import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Switch,
  FormControlLabel,
  Box,
  Typography,
  InputAdornment,
  Divider,
  RadioGroup,
  Radio,
  Chip,
  Alert,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Service, CreateServiceDTO, MeetingProviderType } from '../../types/service';
import { useServiceActions } from '../../hooks/service/useServiceActions';
import { useServices } from '../../hooks/service/useServices';
import { selectPaymentSettings } from '../../store/slices/paymentSlice';
import { useMeetingProviders } from '../../hooks/meeting/useMeetingProviders';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';

interface ServiceFormData extends Omit<CreateServiceDTO, 'priceCents'> {
  price: string; // Store as string for input, convert to cents on submit
  meetingProvider: MeetingProviderType;
}

interface ServiceFormProps {
  service?: Service;
  onClose: () => void;
}

export default function ServiceForm({ service, onClose }: ServiceFormProps) {
  const { createService, updateService, isLoading } = useServiceActions();
  const { refetch } = useServices();
  const paymentSettings = useSelector(selectPaymentSettings);
  const { isZoomConnected, isGoogleMeetConnected } = useMeetingProviders();
  const navigate = useNavigate();
  const selectedOrganization = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation
  );
  const isOnlineMeetingEnabled = selectedOrganization?.isOnlineMeetingEnabled ?? false;

  // Check if Stripe is connected and charges are enabled
  const isStripeConnected =
    paymentSettings?.onboardingStatus === 'COMPLETE' && paymentSettings?.chargesEnabled;

  // Convert cents to dollars for display
  const centsToPrice = (cents: number | null | undefined): string => {
    if (cents === null || cents === undefined) return '';
    return (cents / 100).toFixed(2);
  };

  const { control, handleSubmit, watch } = useForm<ServiceFormData>({
    defaultValues: service
      ? {
          name: service.name,
          description: service.description,
          duration: service.duration,
          isAddonService: service.isAddonService || false,
          isNewUserService: service.isNewUserService || false,
          price: centsToPrice(service.priceCents),
          currency: service.currency || 'AUD',
          meetingProvider: service.meetingProvider || 'none',
        }
      : {
          name: '',
          description: '',
          duration: '30',
          isAddonService: false,
          isNewUserService: false,
          price: '',
          currency: 'AUD',
          meetingProvider: 'none',
        },
  });

  const watchMeetingProvider = watch('meetingProvider');

  const onSubmit = async (data: ServiceFormData) => {
    try {
      // Convert price to cents for API
      const { price, meetingProvider, ...rest } = data;
      const serviceData: CreateServiceDTO = {
        ...rest,
        priceCents: price ? Math.round(parseFloat(price) * 100) : null,
        currency: isStripeConnected ? data.currency || 'AUD' : undefined,
        meetingProvider: meetingProvider || 'none',
      };

      if (service) {
        await updateService(service.id, serviceData);
      } else {
        await createService(serviceData);
      }
      await refetch();
      onClose();
    } catch (error) {
      console.error('Failed to save service:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DialogTitle>{service ? 'Edit Service' : 'Add Service'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Controller
              name="name"
              control={control}
              rules={{ required: 'Name is required' }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Name"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="description"
              control={control}
              rules={{ required: 'Description is required' }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="duration"
              control={control}
              rules={{
                required: 'Duration is required',
                min: { value: 15, message: 'Minimum duration is 15 minutes' },
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Duration (minutes)"
                  type="number"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  inputProps={{ min: 15, step: 15 }}
                />
              )}
            />
          </Grid>
          {isStripeConnected && (
            <Grid item xs={12}>
              <Controller
                name="price"
                control={control}
                rules={{
                  validate: value => {
                    if (value === '' || value === null || value === undefined) return true;
                    const num = parseFloat(value);
                    if (isNaN(num)) return 'Please enter a valid price';
                    if (num < 0) return 'Price cannot be negative';
                    return true;
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Price"
                    type="number"
                    fullWidth
                    error={!!error}
                    helperText={
                      error?.message || 'Price without GST/Tax. Leave empty for free services'
                    }
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Controller
              name="isAddonService"
              control={control}
              render={({ field }) => (
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={field.value}
                        onChange={e => field.onChange(e.target.checked)}
                        disabled={!!service}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight={500}>
                        Is Addon Service?
                      </Typography>
                    }
                    sx={{ ml: 0 }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', ml: 6 }}
                  >
                    Shows as an additional option for a parent service. You can map this in
                    Settings.
                  </Typography>
                </Box>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="isNewUserService"
              control={control}
              render={({ field }) => (
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={field.value}
                        onChange={e => field.onChange(e.target.checked)}
                        disabled={!!service}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight={500}>
                        Is New User Service?
                      </Typography>
                    }
                    sx={{ ml: 0 }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', ml: 6 }}
                  >
                    Shows this service only for new users booking for the first time.
                  </Typography>
                </Box>
              )}
            />
          </Grid>

          {/* Online Meeting Section */}
          {isOnlineMeetingEnabled && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Online Meeting
              </Typography>
              <Controller
                name="meetingProvider"
                control={control}
                render={({ field }) => (
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value !== 'none'}
                          onChange={e => {
                            if (e.target.checked) {
                              // Default to google_meet if connected, else zoom
                              if (isGoogleMeetConnected) {
                                field.onChange('google_meet');
                              } else if (isZoomConnected) {
                                field.onChange('zoom');
                              } else {
                                field.onChange('google_meet');
                              }
                            } else {
                              field.onChange('none');
                            }
                          }}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" fontWeight={500}>
                          This is an online service
                        </Typography>
                      }
                      sx={{ ml: 0 }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', ml: 6, mb: 1 }}
                    >
                      A meeting link will be automatically created when a booking is made.
                    </Typography>

                    {field.value !== 'none' && (
                      <Box sx={{ ml: 6 }}>
                        <RadioGroup
                          value={field.value}
                          onChange={e => field.onChange(e.target.value as MeetingProviderType)}
                        >
                          <FormControlLabel
                            value="google_meet"
                            control={<Radio size="small" />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2">Google Meet</Typography>
                                <Chip
                                  label={isGoogleMeetConnected ? 'Connected' : 'Not connected'}
                                  size="small"
                                  color={isGoogleMeetConnected ? 'success' : 'warning'}
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              </Box>
                            }
                          />
                          <FormControlLabel
                            value="zoom"
                            control={<Radio size="small" />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2">Zoom</Typography>
                                <Chip
                                  label={isZoomConnected ? 'Connected' : 'Not connected'}
                                  size="small"
                                  color={isZoomConnected ? 'success' : 'warning'}
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              </Box>
                            }
                          />
                        </RadioGroup>

                        {watchMeetingProvider !== 'none' &&
                          ((watchMeetingProvider === 'zoom' && !isZoomConnected) ||
                            (watchMeetingProvider === 'google_meet' && !isGoogleMeetConnected)) && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              <Typography variant="caption">
                                This provider is not connected yet.{' '}
                                <Typography
                                  variant="caption"
                                  component="span"
                                  sx={{
                                    color: 'primary.main',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                  }}
                                  onClick={() => navigate('/dashboard/settings/integrations')}
                                >
                                  Go to Settings &gt; Integrations
                                </Typography>{' '}
                                to connect it.
                              </Typography>
                            </Alert>
                          )}
                      </Box>
                    )}
                  </Box>
                )}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="submit"
          variant="contained"
          disableElevation
          disabled={isLoading}
          startIcon={isLoading && <CircularProgress size={20} />}
        >
          {service ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </form>
  );
}
